import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface JobDescriptionPasteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProcessed: (extractedData: any) => void;
}

export const JobDescriptionPasteModal = ({
  isOpen,
  onClose,
  onProcessed
}: JobDescriptionPasteModalProps) => {
  const [jobDescription, setJobDescription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {

    e.preventDefault();
    
    if (!jobDescription.trim()) {
      toast({
        title: t("jobs.uploadModal.noFileSelected"),
        description: t("jobs.uploadModal.pleaseUpload"),
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {

      // Call the analyze-job-offer function to process the job description
      const { data, error } = await supabase.functions.invoke('quick-scan-job-offer', {
        body: { jobOfferText: jobDescription }
      });

      if (error) {
        console.error("Error analyzing job description:", error);
        toast({
          title: t("jobs.uploadModal.processingFailed"),
          description: t("jobs.uploadModal.extractionFailed"),
          variant: "destructive"
        });
      } else {
        console.log("Extracted job data:", data);
        toast({
          title: t("jobs.uploadModal.processed"),
          description: t("jobs.uploadModal.extractionSuccess")
        });
        
        onProcessed(data);
      }
    } catch (error) {
      console.error("Exception analyzing job description:", error);
      toast({
        title: t("jobs.uploadModal.processingFailed"),
        description: t("jobs.uploadModal.unexpectedError"),
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t("jobs.jobOfferModal.pasteOption")}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 flex-grow flex flex-col">
          <p className="text-sm text-muted-foreground">
            {t("jobs.jobOfferModal.pasteDesc")}
          </p>
          
          <Textarea 
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder={t("jobs.jobOfferModal.pasteOptionDesc")}
            className="flex-grow min-h-[400px]"
          />
          
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isProcessing}
            >
              {t("jobs.jobOfferModal.cancel")}
            </Button>
            <Button 
              type="submit"
              disabled={isProcessing || !jobDescription.trim()}
            >
              {isProcessing ? t("jobs.jobOfferModal.processing") : t("jobs.jobOfferModal.extractInfo")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
